/**
 * PdfActivityDialog — modal wrapper for the PdfActivityViewer.
 * Fetches the PDF as a Blob (with Authorization) and hands the object URL to the viewer.
 *
 * Fase 2 additions:
 *  - `reviewActivityId`: fetches the Feedback Hub review and shows overlays
 *    in read-only mode. Used by the teacher in "Revisar trabajos enviados".
 *  - `correctionMode`: student-side second-pass. Same overlays visible, but
 *    the AcroForm widgets stay editable and the primary CTA becomes
 *    "Enviar corrección". Requires `reviewActivityId` (used as the target
 *    activity for POST /manual-correction).
 *  - Teacher-only header action: «Enviar al alumno» → POST /publish. Only
 *    visible while the review is in state "Pendiente de revisión".
 *  - Backwards compatible: legacy call sites that pass `pdfFile={...}` still work.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { PdfActivityViewer } from './PdfActivityViewer';
import { PdfActivityErrorBoundary } from './PdfActivityErrorBoundary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PdfActivityDialog({
  open, onClose,
  contentId, pdfFile, fileName, token,
  reviewActivityId,
  // Fase 2 · Student manual correction (2nd pass). When true, the viewer
  // becomes editable while still showing FH overlays; the CTA switches to
  // "Enviar corrección".
  correctionMode = false,
  // Optional callback: fires after the teacher clicks "Enviar al alumno"
  // (or after the student clicks "Enviar corrección"). The parent uses it
  // to refresh its own list so the state pill updates.
  onPublished,
  onCorrectionSent,
  // Fires right after the student's activity submit succeeds, so the parent
  // card can flip to «Actividad entregada» without waiting for a refetch.
  onActivitySubmitted,
  // iter74 · true only for Lecciones / Tareas / Refuerzo Académico.
  requireAudioBeforeSubmit = false,
}) {
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [review, setReview] = useState(null);
  const [publishing, setPublishing] = useState(false);
  // Local copy of published_to_student so the header action updates without
  // waiting for the parent to refetch.
  const [publishedLocal, setPublishedLocal] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let revokeUrl = null;
    (async () => {
      setPdfObjectUrl(null); setErr(null); setReview(null); setPublishedLocal(null);
      try {
        let filename = pdfFile?.filename;
        if (!filename && contentId) {
          const c = await axios.get(`${API}/content/item/${contentId}?_ts=${Date.now()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const item = c.data || null;
          const f = (item?.files || []).find(f => (f.filename || '').toLowerCase().endsWith('.pdf'));
          filename = f?.filename;
        }
        if (!filename) throw new Error('PDF no disponible');
        const res = await fetch(`${API}/files/${filename}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        revokeUrl = URL.createObjectURL(blob);
        setPdfObjectUrl(revokeUrl);

        if (reviewActivityId) {
          try {
            const rr = await axios.get(`${API}/activities/activity/${reviewActivityId}/review`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!cancelled) {
              setReview(rr.data);
              setPublishedLocal(Boolean(rr.data?.published_to_student));
            }
          } catch (e) {
            if (!cancelled) setErr(`No se pudo cargar la corrección (HTTP ${e?.response?.status || '?'})`);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || 'No se pudo cargar el PDF');
      }
    })();
    return () => { cancelled = true; if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [open, pdfFile, contentId, token, reviewActivityId]);

  const displayTitle = fileName || pdfFile?.original_name || 'Actividad';

  // Header action «Enviar al alumno» — only for the teacher, only while
  // the review is unpublished (state «Pendiente de revisión»).
  const canPublish = (
    !correctionMode
    && reviewActivityId
    && review
    && publishedLocal === false
  );
  const alreadyPublished = publishedLocal === true;
  const publishReview = async () => {
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/activities/activity/${reviewActivityId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPublishedLocal(true);
      toast.success('Corrección enviada al alumno');
      onPublished?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar al alumno');
    }
    setPublishing(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 py-3 pr-12 border-b flex flex-row items-center justify-between gap-2">
          <DialogTitle data-testid="pdf-activity-dialog-title" className="flex-1 min-w-0 truncate">
            {displayTitle}
          </DialogTitle>
          {canPublish && (
            <Button
              data-testid="publish-review-btn"
              onClick={publishReview}
              disabled={publishing}
              size="sm"
              className="gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white mr-4"
            >
              <Send className="w-4 h-4" />
              {publishing ? 'Enviando…' : 'Enviar al alumno'}
            </Button>
          )}
          {alreadyPublished && !correctionMode && (() => {
            // Fase 2 · Coherent header pill with the 3-state contract.
            // Once the student sends the manual correction the state moves
            // from «Enviada al alumno» → «Corrección recibida»; the pill
            // inside the viewer header must match the row badge outside.
            const received = review?.manual_correction?.state === 'received';
            return (
              <span
                data-testid="publish-review-published-pill"
                data-state={received ? 'received' : 'sent'}
                className={`text-[11px] px-2 py-1 rounded-full border mr-4 ${received
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-900'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}
              >
                {received ? 'Corrección recibida' : 'Enviada al alumno'}
              </span>
            );
          })()}
        </DialogHeader>
        <div className="flex-1 overflow-hidden p-3">
          {err && <div className="text-red-600 text-sm p-4">Error: {err}</div>}
          {!err && !pdfObjectUrl && <div className="p-4 text-slate-500 text-sm">Cargando PDF…</div>}
          {/* Fase 2 · When a `reviewActivityId` is set, we must wait for the
              /review response BEFORE mounting PdfActivityViewer. Otherwise
              the viewer's one-shot preload effect runs with review=null
              and misses `original_answers` / `manual_correction.answers`. */}
          {pdfObjectUrl && (!reviewActivityId || review) && (
            <PdfActivityErrorBoundary onReset={onClose}>
              <PdfActivityViewer
                contentId={contentId}
                pdfUrl={pdfObjectUrl}
                token={token}
                review={review}
                readOnly={Boolean(reviewActivityId) && !correctionMode}
                correctionMode={correctionMode}
                correctionActivityId={correctionMode ? reviewActivityId : null}
                onSubmitted={correctionMode ? onCorrectionSent : onActivitySubmitted}
                requireAudioBeforeSubmit={requireAudioBeforeSubmit}
                allowOverride={Boolean(reviewActivityId) && !correctionMode}
                overrideActivityId={reviewActivityId || null}
                onOverrideChanged={async () => {
                  // Refetch the review so overlays repaint with the new
                  // effective status + updated summary. Cheap enough to run
                  // for every override click; no FH round-trip is triggered.
                  try {
                    const rr = await axios.get(
                      `${API}/activities/activity/${reviewActivityId}/review`,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    setReview(rr.data);
                  } catch { /* keep prior review */ }
                }}
              />
            </PdfActivityErrorBoundary>
          )}
          {pdfObjectUrl && reviewActivityId && !review && !err && (
            <div className="p-4 text-slate-500 text-sm">Cargando corrección…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PdfActivityDialog;
